/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description

  This class is the Alternative Satellites in FOV container. It maintains the collapsible list of Satellites in FOV
 */
import React from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import isNil from 'lodash/isNil';
import { connect, shallowEqual, useSelector } from 'react-redux';
import styled from 'styled-components';
import { NetworkGroup, SatelliteInFov, TerminalDetails } from '../../../../shared/entities/terminal';
import { RootState, selectSteerableBeamsRingList } from '../../../../store';
import { isOnline } from '../../../../utils/ConnectionStatusUtils';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../../utils/FeatureFlagUtils';
import { getTerminalSatelliteList } from '../../../../utils/TerminalUtilities';
import StyledProps from '../../../common/theme/AppThemeInterface';
import { HighlightedSatelliteInFov } from '../TabbedPanel/TerminalTabPanel';
import SatelliteInFovListItem from './SatelliteInFovListItem';

export interface SatellitesInFovContainerProps extends StateProps {
  terminalDetails?: TerminalDetails;
  isOverviewMode: boolean;
  satellitesInFOVIndexOffset?: number;
  highlightedSatelliteInFov: HighlightedSatelliteInFov | undefined;
  setHighlightedSatelliteInFov: Function;
}

function SatellitesInFovContainer({
  terminalDetails,
  isOverviewMode,
  satellitesInFOVIndexOffset = 0,
  highlightedSatelliteInFov,
  setHighlightedSatelliteInFov,
  appConfig,
}: SatellitesInFovContainerProps) {
  const steerableBeamsRingList = useSelector(selectSteerableBeamsRingList, shallowEqual);
  const handleClickAway = () => {
    if (highlightedSatelliteInFov && highlightedSatelliteInFov.sticky && highlightedSatelliteInFov.setBySatelliteList) {
      setHighlightedSatelliteInFov(undefined);
    }
  };

  const handleMouseLeave = () => {
    if (!(highlightedSatelliteInFov && highlightedSatelliteInFov.sticky)) {
      setHighlightedSatelliteInFov(undefined);
    }
  };

  const isHighlighted = (satellite: SatelliteInFov, satelliteIdx: number) => {
    if (!isOnline(satellite.connectivityStatus)) {
      return false;
    } else {
      return highlightedIdx !== -1 ? highlightedIdx === satelliteIdx : true;
    }
  };

  const isSatellitesInFOVEnabled = isFeatureEnabled(appConfig, FEATURE_FLAGS.isSatellitesInFOVEnabled);
  let satellites = getTerminalSatelliteList(terminalDetails, isOverviewMode, isSatellitesInFOVEnabled);
  let highlightedIdx = satellites.findIndex(
    (satellite: SatelliteInFov) => satellite.satelliteName === (highlightedSatelliteInFov || {}).satelliteName,
  );
  let isStickyMode = highlightedSatelliteInFov && highlightedSatelliteInFov.sticky;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <StyledSatellitesInFovContainerWrapper onMouseLeave={handleMouseLeave}>
        {!isNil(terminalDetails) && (
          <React.Fragment>
            {satellites.length > 0
              ? satellites.map((satellite: SatelliteInFov, index: number) => (
                  <SatelliteInFovListItem
                    key={satellite.satelliteName}
                    hideLink={false}
                    hideStats={terminalDetails.networkGroup === NetworkGroup.GX}
                    satelliteInFov={satellite}
                    beamName={terminalDetails.beamName}
                    steerableBeamsRingList={
                      !isNil(satellite.steerableBeamsSummary?.beams)
                        ? satellite.steerableBeamsSummary!.beams
                        : steerableBeamsRingList
                    }
                    index={index + satellitesInFOVIndexOffset}
                    isHighlighted={isHighlighted(satellite, index)}
                    isStickyMode={isStickyMode}
                    setHighlightedSatelliteInFov={setHighlightedSatelliteInFov}
                  />
                ))
              : null}
          </React.Fragment>
        )}
      </StyledSatellitesInFovContainerWrapper>
    </ClickAwayListener>
  );
}

const mapStateToProps = (state: RootState) => ({
  appConfig: state.config.appConfig,
});
type StateProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(SatellitesInFovContainer);

const StyledSatellitesInFovContainerWrapper = styled.div`
  display: block;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  overflow: visible;
  padding-bottom: 12px;
  box-sizing: border-box;
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor} !important;
`;
